import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { BadgeComponent } from '@app/shared/components/badge/badge.component';
import { EventModel } from '@app/events/shared/event.model';
import { CurrencyPipe, DatePipe, NgClass } from '@angular/common';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { Edit, LucideAngularModule, Plus, Tag, Trash, FileText } from 'lucide-angular';
import { ProductModelUtils } from '@app/products/shared/product.model';
import { EventTimelineItemModel } from '@app/events/shared/event-timeline-item.model';
import { EventProductItemModel } from '@app/events/shared/event-product-item.model';
import { RightSidebarComponent } from '@app/partials/right-sidebar/right-sidebar.component';
import { TimelineItemAddComponent } from '@app/events/view/timeline-item-add.component';
import { EventService } from '@app/events/shared/event.service';
import { ModalComponent } from '@app/partials/modal/modal.component';
import { TimelineItemEditComponent } from '@app/events/view/timeline-item-edit.component';
import { CreateTemplateFromEventComponent } from '@app/event-templates/create-from-event/create-template-from-event.component';
import { RouterLink } from '@angular/router';
import { AuthService } from '@app/auth/auth.service';
import { ConfirmationModalService } from '@app/partials/modal/confirmation-modal.service';
import { take } from 'rxjs';

@Component({
  selector: 'app-itinerary',
  imports: [
    BadgeComponent,
    DatePipe,
    ButtonComponent,
    LucideAngularModule,
    RightSidebarComponent,
    TimelineItemAddComponent,
    ModalComponent,
    TimelineItemEditComponent,
    CurrencyPipe,
    CreateTemplateFromEventComponent,
    RouterLink,
    NgClass,
  ],
  templateUrl: './itinerary.component.html',
  standalone: true,
})
export class ItineraryComponent implements OnInit, OnChanges {
  @Input() public event!: EventModel;
  @Input() public reloadToken = 0;
  @Input() public scrollToTimelineItemId?: string;
  @Input() public scrollToken = 0;

  protected groupedTimelineItems: { [day: string]: EventTimelineItemModel[] } = {};
  protected loading = false;
  protected loadError: string | null = null;

  protected readonly Object = Object;
  protected readonly TrashIcon = Trash;
  protected readonly ProductModelUtils = ProductModelUtils;
  protected readonly EditIcon = Edit;
  protected readonly Tag = Tag;
  protected readonly Plus = Plus;
  protected readonly FileTemplateIcon = FileText;

  protected deletingTimelineItem?: EventTimelineItemModel;
  protected editingTimelineItem?: EventTimelineItemModel;

  protected isRightSidebarOpen = false;
  protected rightSideBarContent?: string;
  protected canManageTimeline = false;
  protected canCreateTemplate = false;

  constructor(
    private eventService: EventService,
    private authService: AuthService,
    private confirmationModal: ConfirmationModalService,
  ) {
    this.authService.getUser().pipe(take(1)).subscribe({
      next: (user) => {
        const roles = user?.roles || [];
        this.canManageTimeline = roles.includes('admin') || roles.includes('manager');
        this.canCreateTemplate = roles.includes('admin');
      },
      error: () => {
        this.canManageTimeline = false;
        this.canCreateTemplate = false;
      }
    });
  }

  ngOnInit() {
    this.loadTimelineItems();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reloadToken'] && !changes['reloadToken'].firstChange) {
      this.loadTimelineItems();
    }

    if (changes['scrollToken'] && this.scrollToTimelineItemId && !this.loading) {
      this.scrollToTimelineItem(this.scrollToTimelineItemId);
    }
  }

  /**
   * Reload the timeline items.
   *
   * @param options.preserveScroll When true, the list is refreshed in place
   *   without toggling the full-page loading spinner and the current window
   *   scroll position is restored afterwards. This stops the page from jumping
   *   back to the top when a line item is edited (TVG-153).
   */
  private loadTimelineItems(options: { preserveScroll?: boolean } = {}): void {
    if (!this.event?.id) {
      return;
    }

    const preserveScroll = options.preserveScroll ?? false;
    const previousScrollY = preserveScroll ? window.scrollY : null;

    // Skip the full-page spinner on in-place refreshes so the list does not
    // collapse and reset the scroll position.
    this.loading = !preserveScroll;
    this.loadError = null;

    this.eventService.getTimelineItems(this.event.id).subscribe({
      next: (response) => {
        this.event.timeline_items = response.data ?? [];
        this.groupTimelineItemsByDay();
        this.loading = false;
        if (previousScrollY !== null) {
          this.restoreScrollPosition(previousScrollY);
        }
        if (this.scrollToTimelineItemId) {
          this.scrollToTimelineItem(this.scrollToTimelineItemId);
        }
      },
      error: (error) => {
        this.loading = false;
        this.loadError = 'Failed to load itinerary.';
        console.error('Error loading timeline items:', error);
      },
    });
  }

  private groupTimelineItemsByDay(): void {
    if (!this.event?.timeline_items) {
      this.groupedTimelineItems = {};
      return;
    }

    const formatter = new Intl.DateTimeFormat('en-GB', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });

    this.groupedTimelineItems = this.event.timeline_items.reduce(
      (acc, item) => {
        const date = new Date(item.datetime);
        const day = formatter.format(date); // Format as 'EEEE MMMM d yyyy'
        if (!day) return acc;

        if (!acc[day]) {
          acc[day] = [];
        }
        acc[day].push(item);

        return acc;
      },
      {} as { [day: string]: EventTimelineItemModel[] },
    );

    // Sort items in each group by datetime
    Object.keys(this.groupedTimelineItems).forEach((day) => {
      this.groupedTimelineItems[day].sort((a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime());
    });
  }

  private restoreScrollPosition(scrollY: number): void {
    // Wait for the refreshed list to render before restoring the scroll offset.
    setTimeout(() => {
      window.scrollTo({ top: scrollY });
    }, 0);
  }

  private scrollToTimelineItem(itemId: string): void {
    setTimeout(() => {
      const element = document.getElementById(itemId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 0);
  }

  openRightSidebar(content: string) {
    if ((content === 'timeline-item-add' || content === 'timeline-item-edit') && !this.canManageTimeline) {
      return;
    }

    if (content === 'create-template-from-event' && !this.canCreateTemplate) {
      return;
    }

    this.isRightSidebarOpen = true;
    this.rightSideBarContent = content;
  }

  closeRightSidebar() {
    this.isRightSidebarOpen = false;
    this.rightSideBarContent = undefined;
  }

  requestDeleteTimelineItem(item: EventTimelineItemModel) {
    if (!this.canManageTimeline) {
      return;
    }
    if (this.hasProductsAttached(item)) {
      return;
    }
    this.deletingTimelineItem = item;
  }

  confirmDeleteTimelineItem() {
    if (!this.canManageTimeline) {
      return;
    }
    if (this.deletingTimelineItem) {
      this.eventService.deleteEventTimelineItem(this.deletingTimelineItem.id).subscribe({
        next: () => {
          this.event.timeline_items = this.event.timeline_items.filter(
            (item) => item.id !== this.deletingTimelineItem?.id,
          );
          this.deletingTimelineItem = undefined;
          this.groupTimelineItemsByDay();
        },
        error: (res) => {
          console.error('Error deleting timeline item:', res);
          if (res.status === 403) {
            this.confirmationModal.alert({
              title: 'Cannot delete timeline item',
              message: res.error?.message || 'Cannot delete timeline item with products attached',
              confirmLabel: 'OK',
            });
          }
        },
      });
    }
  }

  timelineItemCreated(_item: EventTimelineItemModel) {
    this.loadTimelineItems();
  }

  timelineItemUpdated(_item: EventTimelineItemModel) {
    this.loadTimelineItems({ preserveScroll: true });
  }

  editTimelineItem(item: EventTimelineItemModel) {
    if (!this.canManageTimeline) {
      return;
    }
    this.editingTimelineItem = item;
    this.openRightSidebar('timeline-item-edit');
  }

  createTemplateFromEvent() {
    if (!this.canCreateTemplate) {
      return;
    }
    this.openRightSidebar('create-template-from-event');
  }

  onTemplateCreated() {
    this.closeRightSidebar();
    // Optionally refresh or show success message
  }

  get hasTimelineItems(): boolean {
    return this.event?.timeline_items && this.event.timeline_items.length > 0;
  }

  hasProductsAttached(item: EventTimelineItemModel): boolean {
    return item.event_product_items && item.event_product_items.length > 0;
  }

  getProductDisplayPrice(productItem: EventProductItemModel): number {
    if (!productItem) {
      return 0;
    }
    const calculated = productItem.calculated_price ?? null;
    if (calculated !== null && calculated !== undefined) {
      return calculated;
    }
    if (productItem.amount !== null && productItem.amount !== undefined) {
      return productItem.amount;
    }
    return 0;
  }
}
